import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../assets/Icons";
import SmallPopupWithInput from "../../../../components/_utils/CommonSmallPopupWithInput";
import StatusContainer from "../../../../components/_utils/CommonStatus/StatusContainer";
import InfoCard from "../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import TabsWeapperSection from "../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import CommonToast from "../../../../components/_utils/CommonToast";
import EditCommonPopup from "../../../../components/_utils/CommonPopups/CommonEditPopup";
import UniversalRefreshToast from "../../../../components/_utils/UniversalRefreshToast";

import {
  approveBeneficiaryAccount,
  beneficiaryAccountDetailsById,
  rejectBeneficiaryAccount,
} from "../../../../redux/features/accounts/beneficiaryAccount/index.js";
import { setHideBreadCrumb } from "../../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import {
  bankLookUp,
  panLookUp,
} from "../../../../redux/features/sharedResources/sharedResources";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";

const getVirtualAccountLabel = (acc) => {
  if (acc?.virtual_account_type === "manual") return "Manually created";
  if (acc?.virtual_account_type === "automated") return "Auto detected";
  if (acc?.virtual_account === "N") return "";
  return "";
};

const isVirtualAccount = (acc) =>
  acc?.virtual_account === "Y" || Boolean(getVirtualAccountLabel(acc));

function FieldForceBankEditSuccess() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [rejectPopup, setRejectPopup] = useState(false);
  const [showSwitchAccountModal, setShowSwitchAccountModal] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const dispatch = useDispatch();
  const updatedFields = location?.state?.updatedFields || [];
  const lastDetailFetchIdRef = useRef("");
  const lastPanLookupRef = useRef("");
  const lastBankLookupRef = useRef("");
  const [selectedChipIndex, setSelectedChipIndex] = useState("");
  const updatedPreview = useMemo(
    () => location?.state?.updatedPreview || {},
    [location?.state?.updatedPreview],
  );
  const services = useSelector((state) => state.refreshToken.services);
  const [currentAccountDetails, setCurrentAccountDetails] = useState({
    id: "",
    name: "",
    bank_name: "",
    bank_acc_no: "",
    ifsc_code: "",
    category: "",
    pan: "",
    gstin: "",
    contact_no: "",
    email_id: "",
    labels: "",
  });

  useEffect(() => {
    dispatch(setHideBreadCrumb({ hide: true }));

    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [dispatch]);
  const { beneficiaryAccountDetailsData } = useSelector(
    (state) => state.beneficiaryAccount,
  );

  const beneAccountSuccessData =
    beneficiaryAccountDetailsData?.beneficiary_accounts[0];

  const routeAccountId = location?.state?.updatedAccountId || id;
  const mergedAccountSuccessData = useMemo(
    () => ({
      ...(beneAccountSuccessData || {}),
      ...(updatedPreview?.bank_acc_no
        ? { bank_acc_no: updatedPreview.bank_acc_no }
        : {}),
      ...(updatedPreview?.ifsc_code
        ? { ifsc_code: updatedPreview.ifsc_code }
        : {}),
      ...(updatedPreview?.bank_account_holder_name
        ? {
            bank_account_holder_full_name:
              updatedPreview.bank_account_holder_name,
            name: updatedPreview.bank_account_holder_name,
          }
        : {}),
      ...(updatedPreview?.pan_no ? { pan_no: updatedPreview.pan_no } : {}),
      ...(updatedPreview?.virtual_account
        ? { virtual_account: updatedPreview.virtual_account }
        : {}),
      ...(updatedPreview?.virtual_account_type
        ? { virtual_account_type: updatedPreview.virtual_account_type }
        : {}),
    }),
    [beneAccountSuccessData, updatedPreview],
  );

  const fieldsToShow = useMemo(() => {
    if (updatedFields.length > GlobalConfiguration.lengthCheckZero) {
      return [...updatedFields];
    }
    return ["bank_acc_no", "ifsc_code"];
  }, [updatedFields]);

  useEffect(() => {
    if (beneAccountSuccessData) {
      const {
        id,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        category,
        pan_no,
        labels,
        gstin_detail,
        contact_no,
        email_id,
      } = beneAccountSuccessData;
      setCurrentAccountDetails({
        id,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        category,
        pan: pan_no,
        labels,
        gstin: gstin_detail || "",
        contact_no,
        email_id,
      });
    }
  }, [beneAccountSuccessData]);

  const viewDetailsHandler = () => {
    navigate(
      `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${routeAccountId}`,
    );
  };

  const dashboardHandler = () => {
    navigate("/field_force_account_dashboard");
  };

  useEffect(() => {
    if (!routeAccountId) return;
    if (lastDetailFetchIdRef.current === routeAccountId) return;
    lastDetailFetchIdRef.current = routeAccountId;
    dispatch(
      beneficiaryAccountDetailsById({
        id: routeAccountId,
      }),
    );
  }, [dispatch, routeAccountId]);

  useEffect(() => {
    if (!beneAccountSuccessData) return;
    const panLookupKey = `${beneAccountSuccessData.pan_no || ""}`;
    if (panLookupKey && lastPanLookupRef.current !== panLookupKey) {
      lastPanLookupRef.current = panLookupKey;
      dispatch(panLookUp({ pan_number: beneAccountSuccessData.pan_no }));
    }

    const bankLookupKey = `${beneAccountSuccessData.ifsc_code || ""}-${beneAccountSuccessData.bank_acc_no || ""}`;
    if (
      beneAccountSuccessData.ifsc_code &&
      beneAccountSuccessData.bank_acc_no
    ) {
      if (lastBankLookupRef.current !== bankLookupKey) {
        lastBankLookupRef.current = bankLookupKey;
        dispatch(
          bankLookUp({
            ifsc: beneAccountSuccessData.ifsc_code,
            bank_account_no: beneAccountSuccessData.bank_acc_no,
          }),
        );
      }
    } else {
      lastBankLookupRef.current = "";
    }
  }, [beneAccountSuccessData, dispatch]);

  const accountApproval = () => {
    (async () => {
      setShowLoader();
      toast.dismiss();
      UniversalRefreshToast("approvalaccountsRefresh");
      let response = await dispatch(approveBeneficiaryAccount({ id }));
      if (approveBeneficiaryAccount.fulfilled.match(response)) {
        toast.dismiss();
        CommonToast({
          text: "Beneficiary account approved.",
          variant: "var(--success)",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        });

        navigate(
          `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
          { replace: true },
        );
      } else {
        toast.dismiss();
        CommonToast({
          text: response?.payload?.errors?.beneficiary_account?.[0],
          variant: "var(--error)",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        });
      }
    })();
  };

  const accountRejected = () => {
    (async () => {
      setShowLoader();
      toast.dismiss();
      UniversalRefreshToast("accountsRefresh");
      let response = await dispatch(
        rejectBeneficiaryAccount({
          id,
          rejection_reason: declineReason,
        }),
      );
      if (rejectBeneficiaryAccount.fulfilled.match(response)) {
        toast.dismiss();
        CommonToast({
          text: "Beneficiary account rejected.",
          variant: "var(--success)",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        });

        navigate(
          `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
          { replace: true },
        );
      } else {
        toast.dismiss();
        CommonToast({
          text: response?.payload?.errors?.beneficiary_account?.[0],
          variant: "var(--error)",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        });
        setRejectPopup(false);
      }
    })();
  };
  const bankFieldConfig = {
    bank_account_holder_name: {
      leftSubTitle: "Account name",
      getTitle: (acc) => acc?.bank_account_holder_full_name || acc?.name,
    },
    bank_acc_no: {
      leftSubTitle: "Account number",
      getTitle: (acc) => acc?.bank_acc_no,
      ifsc: (acc) => acc?.ifsc_code,
    },
    ifsc_code: {
      leftSubTitle: "IFSC",
      getTitle: (acc) => acc?.ifsc_code,
    },
    virtual_account_type: {
      leftSubTitle: "Virtual account",
      getTitle: (acc) => getVirtualAccountLabel(acc),
    },
  };
  const handleInputChange = (event) => {
    const sanitizedValue = event.target.value
      .replace(/\s+/g, " ")
      .replace(/^\s/, "")
      .slice(0, 150);

    setDeclineReason(sanitizedValue);
  };
  const switchAccountType = () => {
    setShowSwitchAccountModal(true);
  };

  const renderBankCards = () => {
    return fieldsToShow
      .filter((key) => {
        if (!bankFieldConfig[key]) return false;
        if (key === "virtual_account_type") {
          return isVirtualAccount(mergedAccountSuccessData);
        }
        return true;
      })
      .map((key) => {
        const config = bankFieldConfig[key];
        const title = config.getTitle(mergedAccountSuccessData);
        if (!title) return null;
        return (
          <InfoCard
            key={key}
            bgColor="var(--positive-card)"
            rows={[
              {
                leftTitle: title,
                leftSubTitle: config.leftSubTitle,
                ...(config.ifsc && {
                  ifsc: config.ifsc(mergedAccountSuccessData),
                }),
              },
            ]}
          />
        );
      });
  };
  const handleChipClick = (id) => {
    if (selectedChipIndex === id) {
      setSelectedChipIndex("");
    } else {
      setSelectedChipIndex(id);
    }
  };
  const handleApply = (index) => {
    setShowSwitchAccountModal(false);
    if (index === GlobalConfiguration?.lengthCheckTwo) {
      navigate(`/field_force_account_dashboard/create_field_force_account`, {
        replace: true,
        state: {
          ...currentAccountDetails,
          type: "switch_account",
        },
      });
    } else {
      CommonToast({
        text: "Feature coming soon",
        icon: ICONS?.INFO,
        variant: "var(--coming-soon)",
        buttonNeeded: false,
      });
    }
    setSelectedChipIndex("");
  };
  const isAccountTypeSelected =
    selectedChipIndex !== "" && selectedChipIndex != null;
  const chipsArray = [
    {
      text: "Reimbursement account",
      icon: ICONS?.REIMBURSEMENT_ACCOUNTS_WITH_BLUE_CIRCLE,
      selected: selectedChipIndex === GlobalConfiguration.lengthCheckZero,
      disabled: false,
      hoverOption: "blue",
      bulletPoint: [
        "This account will require approval after switching.",
        "Linking a user is optional for this account",
      ],
      onClick: () => handleChipClick(GlobalConfiguration.lengthCheckZero),
    },
    {
      text: "Field force account",
      icon: ICONS?.FIELD_FORCE_ACCOUNTS_WITH_BLUE_CIRCLE,
      selected: selectedChipIndex === GlobalConfiguration?.lengthCheckTwo,
      disabled: false,
      hoverOption: "blue",
      onClick: () => handleChipClick(GlobalConfiguration?.lengthCheckTwo),
      bulletPoint: [
        "This account will require approval after switching.",
        "Linking a user is required for this account.",
      ],
    },
  ];

  const buttonRows = [
    ...(services?.includes("BAAccount") ||
    (services?.includes("Admin") &&
      beneAccountSuccessData?.approval_status === "pending")
      ? [
          [
            {
              btnText: "Reject account",
              enabledBgColor: "var(--negative-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS.REJECT_ICON_RED_WITH_WHITE_CIRCLE,
              handleFunction: () => {
                setRejectPopup(true);
              },
              iconToBeAlign: "left",
              fullWidthRequired: true,
            },
            {
              btnText: "Approve account",
              enabledBgColor: "var(--positive-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
              handleFunction: () => {
                accountApproval();
              },
              iconToBeAlign: "left",
              fullWidthRequired: true,
            },
          ],
        ]
      : []),
    [
      {
        btnText: "Switch account",
        enabledBgColor: "var(--text-primary)",
        enabledTextColor: "var(--positive-bg)",
        icon: ICONS?.SWITCH_ACCOUNT,
        handleFunction: switchAccountType,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        imgColor: "var(--positive-bg)",
      },

      {
        btnText: "View details",
        enabledBgColor: "var(--text-primary)",
        enabledTextColor: "var(--positive-bg)",
        icon: ICONS.VIEW_DETAILS,
        handleFunction: viewDetailsHandler,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        imgColor: "var(--positive-bg)",
      },
    ],

    [
      {
        btnText: "Go to dashboard",
        enabledBgColor: "var(--bg-positive-bg)",
        enabledTextColor: "var(--text-primary)",
        icon: ICONS.DASHBOARD,
        handleFunction: dashboardHandler,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        outLineColor: "var(--text-primary)",
      },
    ],
  ];

  return (
    <div>
      {rejectPopup && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-max-zindex bg-custom-backdrop bg-opacity-dark-opacity">
          <SmallPopupWithInput
            headerTitle="Rejected reason"
            subtext="Please state the reason for rejecting the account"
            headerFontColor="text-primary"
            headerFontWeight="semibold"
            textSize="font-md"
            inputPlaceholder="Reason(10-150 char)"
            inputBgColor="var(--primary-bg)"
            inputFgColor="var(--text-secondary)"
            inputOnInput={(e) => {
              handleInputChange(e);
            }}
            inputValue={declineReason}
            inputFontKind="small"
            inputIsFullWidth={true}
            btnOneHandleFunction={() => {
              setRejectPopup(false);
              setDeclineReason("");
            }}
            btnOneText="Cancel"
            btnOneBtnType="button"
            btnOneEnabledBgColor="trans"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={
              !declineReason ||
              declineReason.length < GlobalConfiguration.panLength
            }
            btnOneIsDisabled={showLoader ? true : false}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption="black"
            btnTwoText="Reject"
            btnTwoHandleFunction={() => {
              accountRejected();
              setDeclineReason("");
            }}
            btnTwoBtnType="submit"
            btnTwoInActiveVariantNeeded={true}
          />
        </div>
      )}
      {showSwitchAccountModal && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <EditCommonPopup
            headerTitle="Select account type to switch"
            subtext="Available account types are listed below."
            chipsArray={chipsArray}
            btnOneBtnType="button"
            btnOneHandleFunction={() => {
              setShowSwitchAccountModal(false);
              setSelectedChipIndex("");
            }}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneHoverOption="white"
            btnOneOutLineColor="var(--text-primary)"
            btnTwoText="Next"
            btnTwoBtnType="button"
            btnTwoHandleFunction={() => {
              handleApply(selectedChipIndex);
            }}
            btnTwoIsDisabled={!isAccountTypeSelected}
            btnTwoInActiveVariantNeeded={true}
            btnTwoDisabledTextColor="var(--text-primary)"
            btnTwoEnabledBgColor="var(--text-primary)"
            btnTwoEnabledTextColor="var(--primary-bg)"
            // btnTwoIsLoaderNeeded={handleLoading}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption="black"
            defaultHeight={isAccountTypeSelected ? selectedChipIndex : null}
            activeAccountType={true}
          />
        </div>
      )}
      <StatusContainer
        status="success"
        buttonRows={buttonRows}
        title={
          "Old account archived and new account added to the list of active beneficiary accounts successfully."
        }
      >
        <TabsWeapperSection title="Updated details">
          <>
            {renderBankCards()}{" "}
            <InfoCard
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: beneAccountSuccessData?.id || "NA",
                  leftSubTitle: "Account ID",
                  copyText: beneAccountSuccessData?.id || "NA",
                },
              ]}
            />
          </>
        </TabsWeapperSection>

        {/* this commented code is required for the future  */}
        {/* {beneLookData?.beneLookData?.beneficiary_accounts?.length >
          GlobalConfiguration.lengthCheckOne && (
          <TabsWeapperSection title="Accounts Associated to PAN">
            {beneLookData?.beneLookData?.beneficiary_accounts
              ?.filter(
                (beneAccountSuccessData) =>
                  beneAccountSuccessData?.approval_status === "pending" ||
                  beneAccountSuccessData?.approval_status === "approved",
              )
              .map((beneAccountSuccessData, idx) => (
                <InfoCard
                  key={beneAccountSuccessData?.id || idx}
                  bgColor="var(--positive-card)"
                  header={true}
                  borderColor="var(--positive-bg)"
                  rightHeaderText={`${
                    beneAccountSuccessData?.approval_status?.charAt(0).toUpperCase() +
                    beneAccountSuccessData?.approval_status?.slice(1)
                  } account`}
                  rightHeaderTextColor="var(--text-secondary)"
                  leftHeaderText={beneAccountSuccessData?.bank_account_holder_full_name}
                  leftHeaderWidth="w-width-lg"
                  rows={[
                    {
                      leftTitle: beneAccountSuccessData?.bank_acc_no,
                      leftSubTitle: "Bank account number",
                      ifsc: beneAccountSuccessData?.ifsc_code,
                    },
                  ]}
                />
              ))}
          </TabsWeapperSection>
        )}

        {benebankLookData?.beneficiary_accounts?.length >
          GlobalConfiguration.lengthCheckOne && (
          <TabsWeapperSection title="Accounts Associated to Bank">
            {benebankLookData?.beneficiary_accounts
              .filter(
                (beneAccountSuccessData) =>
                  beneAccountSuccessData?.approval_status === "pending" ||
                  beneAccountSuccessData?.approval_status === "approved",
              )
              .map((beneAccountSuccessData, idx) => (
                <InfoCard
                  key={beneAccountSuccessData?.id || idx}
                  bgColor="var(--positive-card)"
                  header={true}
                  borderColor="var(--positive-bg)"
                  rightHeaderText={`${
                    beneAccountSuccessData?.approval_status?.charAt(0).toUpperCase() +
                    beneAccountSuccessData?.approval_status?.slice(1)
                  } account`}
                  rightHeaderTextColor="var(--text-secondary)"
                  leftHeaderText={beneAccountSuccessData?.name}
                  leftHeaderWidth="w-width-lg"
                  rows={[
                    {
                      leftTitle: beneAccountSuccessData?.pan_no,
                      leftSubTitle: "PAN number",
                    },
                  ]}
                />
              ))}
          </TabsWeapperSection>
        )} */}
      </StatusContainer>
    </div>
  );
}

export default FieldForceBankEditSuccess;
